import { buildDocument } from "@easyblocks/core";
import { DEMO_DOCUMENT_ID, localBackend } from "../api/adapters/local-backend";
import { resolveExternalDataLocal } from "../api/adapters/local-mocks";
import { resolveExternalData } from "../api";
import { useAppStore } from "../state/app-store";
import { easyblocksConfig } from "./easyblocks-config/index";
import { getSelectedBackend } from "./easyblocks-config/config";

export type AppMode = "editor" | "render";

type ChromeMode = "auto" | "none" | "full";

function applyIframeChromeOverrides(isInIframe: boolean) {
    if (!isInIframe || typeof document === "undefined") return;
    const styleId = "easyblocks-iframe-chrome";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
        #site_header,
        #site_footer {
            display: none !important;
        }
    `;
    document.head.appendChild(style);
    document.body?.classList.add("easyblocks-iframe");
}

export function resolveMode(mountId?: string): AppMode {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get("mode")?.toLowerCase();
    if (mountId === "easyblocks-editor-root") return "editor";
    if (mountId === "easyblocks-render-root") return "render";
    if (modeParam === "editor" || modeParam === "render") return modeParam;
    return "render";
}

export function navigateToRender() {
    const url = new URL(window.location.href);
    url.search = "";
    url.pathname = url.pathname.replace(/\/editor\/?$/, "/");
    window.location.assign(url.toString());
}

export function resolveDocumentContext(mountEl?: HTMLElement | null): {
    documentId?: string;
    locale?: string;
} {
    const params = new URLSearchParams(window.location.search);
    const mountDocumentId = mountEl?.getAttribute("data-document-id") ?? undefined;
    const mountLocale = mountEl?.getAttribute("data-locale") ?? undefined;
    const paramDocumentId =
        params.get("document") ?? params.get("documentId") ?? params.get("doc") ?? undefined;
    const paramLocale = params.get("locale") ?? undefined;

    return {
        documentId: mountDocumentId ?? paramDocumentId,
        locale: mountLocale ?? paramLocale,
    };
}

async function loadDocumentToStore(options: {
    documentId?: string;
    locale?: string;
    setEditorState?: boolean;
}) {
    const { documentId, locale, setEditorState } = options;
    const backend = (easyblocksConfig as any)?.backend;
    const getDocument = backend?.documents?.get;
    if (!documentId || typeof getDocument !== "function") {
        return;
    }

    try {
        const doc = await getDocument({ id: documentId, locale });
        const store = useAppStore.getState();
        store.setActiveDocument(doc);
        if (setEditorState) {
            store.setCurrentDocument(doc);
            store.setBaselineDocument(doc);
        }
    } catch (error) {
        console.error("[main] Failed to load document:", error);
    }
}

async function loadRenderableDocumentToStore(options: {
    documentId?: string;
    locale?: string;
}) {
    const { documentId, locale } = options;
    const resolvedLocale = locale ?? "en-US";
    const store = useAppStore.getState();

    if (!documentId) {
        store.setRenderableDocument(null);
        store.setRenderExternalData({});
        store.setRenderLoading(false);
        return;
    }

    store.setRenderableDocument(null);
    store.setRenderLoading(true);
    try {
        const isLocal = getSelectedBackend() === localBackend;
        const res = await buildDocument({
            documentId,
            config: easyblocksConfig as any,
            locale: resolvedLocale,
        });
        const resolved = isLocal
            ? resolveExternalDataLocal({ externals: res.externalData as any })
            : await resolveExternalData({
                  externals: res.externalData as any,
                  document_id: documentId,
              });
        store.setRenderableDocument(res.renderableDocument);
        store.setRenderExternalData(resolved.externalData ?? {});
        store.setRenderLoading(false);
    } catch (error) {
        console.error("[main] Failed to build/resolve render document:", error);
        store.setRenderLoading(false);
    }
}

export async function initializeAppStore(options: {
    mountEl: HTMLElement;
    runtimeMode: AppMode;
}): Promise<{
    effectiveDocumentId?: string;
    resolvedLocale: string;
    isEmbeddedMount: boolean;
}> {
    const { mountEl, runtimeMode } = options;
    const { documentId: resolvedDocumentId, locale } = resolveDocumentContext(mountEl);
    const resolvedLocale = locale ?? "en-US";
    const isDev = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
    const effectiveDocumentId = resolvedDocumentId ?? (isDev ? DEMO_DOCUMENT_ID : undefined);
    const isEmbeddedMount =
        mountEl.id === "easyblocks-editor-root" || mountEl.id === "easyblocks-render-root";
    const isInIframe = typeof window !== "undefined" && window.parent !== window;
    const editorChromeMode: ChromeMode = "auto";

    const store = useAppStore.getState();
    store.setDocumentContext({
        documentId: effectiveDocumentId ?? null,
        locale: resolvedLocale,
    });
    store.setIsEmbeddedMount(isEmbeddedMount);
    store.setIsInIframe(isInIframe);
    store.setRuntimeMode(runtimeMode);
    store.setEditorChromeMode(editorChromeMode);
    store.setRenderShowChrome(!isEmbeddedMount);
    applyIframeChromeOverrides(isInIframe);

    if (effectiveDocumentId) {
        void loadDocumentToStore({
            documentId: effectiveDocumentId,
            locale: resolvedLocale,
            setEditorState: runtimeMode === "editor",
        });
    } else {
        store.setActiveDocument(null);
        if (runtimeMode === "editor") {
            store.setCurrentDocument(null);
            store.setBaselineDocument(null);
        }
    }

    if (runtimeMode === "render") {
        void loadRenderableDocumentToStore({
            documentId: effectiveDocumentId,
            locale: resolvedLocale,
        });
    }

    return { effectiveDocumentId, resolvedLocale, isEmbeddedMount };
}
